import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CheckCircle2, Phone, Globe, Bot, Shield, DollarSign, Zap } from 'lucide-react';
import SiteFooter from './SiteFooter';
import SEOHead from './SEOHead';

const COLORS = { primary: '#002FA7', dark: '#0a0f1e', accent: '#FFCC00' };

const COMPETITORS = [
  { nameKey: 'BrightLocal', slug: 'brightlocal', tagline: 'Citation specialist — but that\'s all', taglineEs: 'Especialista en citaciones — pero eso es todo', price: '$39-59/mo', weWin: 14, shared: 8, theyWin: 2, weaknesses: ['No call tracking', 'No AI content', 'No billing/dunning', 'No SMS'], weaknessesEs: ['Sin seguimiento de llamadas', 'Sin contenido IA', 'Sin sistema de cobros', 'Sin SMS'] },
  { nameKey: 'Vendasta', slug: 'vendasta', tagline: 'Marketplace giant — with giant fees', taglineEs: 'Gigante del mercado — con tarifas gigantes', price: '$99-999/mo + onboarding', weWin: 12, shared: 7, theyWin: 5, weaknesses: ['$500-1,500 onboarding fees', 'No call tracking', 'No AI blog engine', 'Per-seat charges'], weaknessesEs: ['$500-1,500 de incorporación', 'Sin seguimiento de llamadas', 'Sin blog IA', 'Cargos por asiento'] },
  { nameKey: 'GoHighLevel', slug: 'gohighlevel', tagline: 'CRM powerhouse — missing local SEO', taglineEs: 'Potencia CRM — sin SEO local', price: '$97-497/mo', weWin: 12, shared: 5, theyWin: 7, weaknesses: ['No citation management', 'No call attribution', 'No programmatic SEO pages', 'Hidden usage fees'], weaknessesEs: ['Sin gestión de citaciones', 'Sin atribución de llamadas', 'Sin páginas SEO programáticas', 'Tarifas ocultas'] },
  { nameKey: 'Yext', slug: 'yext', tagline: 'Listings pioneer — premium pricing', taglineEs: 'Pionero en listados — precios premium', price: '$199-999/mo per location', weWin: 16, shared: 5, theyWin: 3, weaknesses: ['$199/mo for basic listings', 'No call tracking', 'No client dashboard', 'No dunning system'], weaknessesEs: ['$199/mes para listados básicos', 'Sin seguimiento de llamadas', 'Sin panel de clientes', 'Sin sistema de cobros'] },
];

const STRENGTH_ICONS = [Phone, Bot, Globe, Shield, DollarSign, Zap];
const STRENGTH_KEYS = ['callTracking', 'aiBlog', 'seoPages', 'dunning', 'sms', 'onePlatform'];

const TABLE_ROWS = [
  ['callTracking', true, false, false, false, false],
  ['citationBuilder', true, true, true, false, true],
  ['aiBlog', true, false, false, false, false],
  ['seoPages', true, false, false, false, false],
  ['dunning', true, false, false, false, false],
  ['whiteLabel', true, true, true, true, false],
  ['sms', true, false, false, true, false],
  ['reviewMonitoring', true, true, true, true, true],
  ['crm', false, false, true, true, false],
  ['listingsSync', true, true, true, false, true],
];

export default function ComparisonHub() {
  const { t, i18n } = useTranslation('compare');
  const isEs = i18n.language === 'es';

  return (
    <>
      <SEOHead
        title={isEs ? "Mejor Plataforma de Marketing Local 2026 — LocalBranding Pro vs Competidores" : "Best Local Marketing Platform 2026 — LocalBranding Pro vs Competitors"}
        description={isEs ? "Compara LocalBranding Pro contra BrightLocal, Vendasta, GoHighLevel y Yext." : "Compare LocalBranding Pro against BrightLocal, Vendasta, GoHighLevel, and Yext."}
        canonical="https://localbrandingpro.com/compare"
      />
      <div style={{ fontFamily: "'Outfit', 'IBM Plex Sans', sans-serif" }}>
        {/* Hero */}
        <section className="relative overflow-hidden" style={{ background: COLORS.dark }}>
          <div className="absolute inset-0 opacity-[0.06]" style={{ background: `radial-gradient(circle at 40% 40%, ${COLORS.primary}, transparent 50%), radial-gradient(circle at 70% 70%, ${COLORS.accent}, transparent 40%)` }} />
          <div className="max-w-6xl mx-auto px-6 py-24 relative z-10 text-center">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] mb-5 px-4 py-1.5 rounded-full" style={{ background: `${COLORS.accent}20`, color: COLORS.accent }}>
              {t('hub.badge')}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="hub-title">
              {t('hub.title')}
              <span className="block mt-2" style={{ color: COLORS.accent }}>{t('hub.titleAccent')}</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10">{t('hub.subtitle')}</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mb-10">
              {COMPETITORS.map(c => (
                <div key={c.slug} className="rounded-xl bg-white/5 border border-white/10 p-4">
                  <p className="text-xs text-slate-500 mb-1">{t('hub.vsPrefix')} {c.nameKey}</p>
                  <p className="text-2xl font-bold" style={{ color: COLORS.accent }}>{c.weWin}</p>
                  <p className="text-xs text-slate-400">{t('hub.exclusiveFeatures')}</p>
                </div>
              ))}
            </div>

            <Link to="/signup" className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-white font-bold text-lg transition-all hover:scale-105" style={{ background: COLORS.primary }} data-testid="cta-hero">
              {t('hub.cta')} <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </section>

        {/* Competitor Cards */}
        <section className="py-16 bg-white" data-testid="competitor-cards">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-3" style={{ color: COLORS.dark }}>{t('hub.headToHead')}</h2>
            <p className="text-center text-slate-500 text-sm mb-12 max-w-xl mx-auto">{t('hub.headToHeadSub')}</p>
            <div className="grid md:grid-cols-2 gap-6">
              {COMPETITORS.map(c => (
                <Link key={c.slug} to={`/compare/${c.slug}`} className="group rounded-xl border border-slate-200 p-6 hover:border-blue-300 hover:shadow-lg transition-all" data-testid={`card-${c.slug}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold group-hover:text-blue-700 transition-colors" style={{ color: COLORS.dark }}>{t('hub.vsPrefix')} {c.nameKey}</h3>
                      <p className="text-sm text-slate-500 mt-1">{isEs ? c.taglineEs : c.tagline}</p>
                    </div>
                    <span className="text-xs font-medium px-3 py-1 rounded-full bg-red-50 text-red-600 whitespace-nowrap">{c.price}</span>
                  </div>
                  <div className="flex gap-4 mb-4">
                    <div className="text-center"><p className="text-lg font-bold" style={{ color: COLORS.primary }}>{c.weWin}</p><p className="text-[10px] text-slate-400 uppercase tracking-wider">{t('hub.weWin')}</p></div>
                    <div className="text-center"><p className="text-lg font-bold text-slate-600">{c.shared}</p><p className="text-[10px] text-slate-400 uppercase tracking-wider">{t('hub.shared')}</p></div>
                    <div className="text-center"><p className="text-lg font-bold text-slate-400">{c.theyWin}</p><p className="text-[10px] text-slate-400 uppercase tracking-wider">{t('hub.theyWin')}</p></div>
                  </div>
                  <div className="space-y-1.5">
                    {(isEs ? c.weaknessesEs : c.weaknesses).map((w, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" style={{ color: COLORS.primary }} />
                        <span>{t('hub.weHaveTheyDont')} <strong>{w}</strong></span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all" style={{ color: COLORS.primary }}>
                    {t('hub.viewFull')} <ArrowRight className="h-4 w-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Our Strengths */}
        <section className="py-16" style={{ background: '#f8fafc' }}>
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-3" style={{ color: COLORS.dark }}>{t('hub.whatMakesDifferent')}</h2>
            <p className="text-center text-slate-500 text-sm mb-10 max-w-xl mx-auto">{t('hub.whatMakesDifferentSub')}</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {STRENGTH_KEYS.map((key, i) => {
                const Icon = STRENGTH_ICONS[i];
                return (
                  <div key={key} className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-md transition-shadow">
                    <Icon className="h-8 w-8 mb-4" style={{ color: COLORS.primary }} />
                    <h3 className="font-bold text-lg mb-2" style={{ color: COLORS.dark }}>{t(`strengths.${key}.title`)}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{t(`strengths.${key}.desc`)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Summary Table */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{ color: COLORS.dark }}>{t('hub.quickComparison')}</h2>
            <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <table className="w-full text-sm" data-testid="summary-table">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left px-5 py-4 font-semibold text-slate-700">{t('hub.capability')}</th>
                    <th className="text-center px-3 py-4 font-bold text-xs" style={{ color: COLORS.primary }}>LBP</th>
                    <th className="text-center px-3 py-4 font-semibold text-xs text-slate-500">BrightLocal</th>
                    <th className="text-center px-3 py-4 font-semibold text-xs text-slate-500">Vendasta</th>
                    <th className="text-center px-3 py-4 font-semibold text-xs text-slate-500">GHL</th>
                    <th className="text-center px-3 py-4 font-semibold text-xs text-slate-500">Yext</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {TABLE_ROWS.map(([featureKey, ...vals], i) => (
                    <tr key={i} className={`hover:bg-slate-50 ${vals[0] && !vals.slice(1).some(Boolean) ? 'bg-blue-50/30' : ''}`}>
                      <td className="px-5 py-3 text-slate-700">{t(`features.${featureKey}`)}</td>
                      {vals.map((v, j) => (
                        <td key={j} className="px-3 py-3 text-center">
                          {v ? <CheckCircle2 className="h-4 w-4 inline text-emerald-500" /> : <span className="text-slate-300">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20" style={{ background: COLORS.dark }}>
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('hub.stopPaying')}</h2>
            <p className="text-slate-400 text-lg mb-8">{t('hub.stopPayingSub')}</p>
            <Link to="/signup" className="inline-flex items-center gap-2 px-10 py-4 rounded-lg font-bold text-lg transition-all hover:scale-105" style={{ background: COLORS.accent, color: COLORS.dark }} data-testid="cta-final">
              {t('hub.startTrial')} <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </section>
        <SiteFooter />
      </div>
    </>
  );
}
